import { useState } from 'react';
import { DatabaseOutlined, SaveOutlined } from '@ant-design/icons';

import { Tile, TileBody, TileHeader } from '../../../../components/shared';
import { Badge, Button, Checkbox, Field, Input, Skeleton } from '../../../../components/ui';
import { useT } from '../../../../i18n';
import { soVN } from '../../../../lib/dinh-dang';
import { useToast } from '../../../../lib/toast';
import { DaiLoi } from '../chung/VungDuLieu';
import { useMoTaLoi } from '../../hooks/useMoTaLoi';
import { useCauHinhDem, useDatCauHinhDem } from '../../hooks/useVanHanh';
import { giayRaPhut, phutRaGiay } from '../../nghiep-vu/van-hanh';
import type { CauHinhDem } from '../../types';

/** Hai cấu hình có cùng giá trị ở mọi núm không. */
function giongNhau(a: CauHinhDem, b: CauHinhDem): boolean {
  return (
    a.bat === b.bat &&
    a.hanGiay === b.hanGiay &&
    a.sucChua === b.sucChua &&
    Boolean(a.demTrucTiep) === Boolean(b.demTrucTiep) &&
    (a.sqlHanGiay ?? 0) === (b.sqlHanGiay ?? 0) &&
    (a.tiengHanGiay ?? 0) === (b.tiengHanGiay ?? 0)
  );
}

/**
 * Các núm đệm lượt hỏi, đặt tại chỗ thay vì trong biến môi trường (port `DemLuot.tsx` native).
 *
 * - Nằm ở màn Nhật ký: chỉnh đệm là việc VẬN HÀNH, người chỉnh cần thấy ngay tỉ lệ trúng và số mục đang giữ cạnh ô nhập.
 * - Giá trị vẽ lại từ PHẢN HỒI máy chủ: máy chủ KẸP giá trị, giữ số vừa gõ sau khi lưu là hiện một con số không có thật.
 * - Nhập bằng PHÚT, gửi bằng GIÂY: máy chủ nói bằng giây (hợp đồng, biến môi trường), người vận hành nghĩ bằng phút.
 */
export function DemLuot() {
  const { t, locale } = useT();
  const toast = useToast();
  const moTaLoi = useMoTaLoi();
  const q = useCauHinhDem();
  const luu = useDatCauHinhDem();
  // `null` = chưa sửa gì, hiện đúng giá trị máy chủ; sửa thì giữ bản nháp tới lúc lưu xong.
  const [nhap, setNhap] = useState<CauHinhDem | null>(null);
  const [daKep, setDaKep] = useState(false);

  const tieuDe = <TileHeader icon={<DatabaseOutlined />} tone="teal" title={t('troLyAiVanHanhDem.tieuDe')} />;

  // ⚠️ Lỗi xét TRƯỚC trạng thái tải: đảo lại thì nhánh báo lỗi không bao giờ chạy và khung xương đứng vĩnh viễn.
  if (q.isError) {
    return (
      <Tile>
        {tieuDe}
        <DaiLoi loi={q.error} onThuLai={() => void q.refetch()} />
      </Tile>
    );
  }
  if (q.isPending) {
    return (
      <Tile aria-busy="true">
        {tieuDe}
        <TileBody>
          <Skeleton className="h-28 w-full" />
        </TileBody>
      </Tile>
    );
  }

  const { gioiHan, nguon, thongKe } = q.data;
  const f = nhap ?? q.data.cauHinh;
  const dat = (thay: Partial<CauHinhDem>) => {
    setNhap({ ...f, ...thay });
    setDaKep(false);
  };
  const [chuaThap, chuaCao] = gioiHan.sucChua;
  const hanThapP = giayRaPhut(gioiHan.hanGiay[0]);
  const hanCaoP = giayRaPhut(gioiHan.hanGiay[1]);
  const [sqlThap, sqlCao] = gioiHan.sqlHanGiay ?? [300, 604_800];
  const [tiengThap, tiengCao] = gioiHan.tiengHanGiay ?? [60, 3600];
  // Hai điều kiện, không một: đặt 5 phút mà máy chủ không có Redis thì lượt có giọng vẫn không được đệm.
  const tiengChay = thongKe?.demTieng ?? false;

  const onLuu = () =>
    luu.mutate(f, {
      onSuccess: (moi) => {
        setDaKep(!giongNhau(moi.cauHinh, f));
        setNhap(null);
        toast.success(t('troLyAiVanHanhDem.daLuu'));
      },
      onError: (e) => toast.error(moTaLoi(e)),
    });

  return (
    <Tile>
      <TileHeader
        icon={<DatabaseOutlined />}
        tone="teal"
        title={t('troLyAiVanHanhDem.tieuDe')}
        mo={t('troLyAiVanHanhDem.moTa')}
        action={
          <Button size="sm" icon={<SaveOutlined />} loading={luu.isPending} onClick={onLuu}>
            {t('troLyAiVanHanhDem.luu')}
          </Button>
        }
      />
      <TileBody className="space-y-3">
        {/* Nói ra khi chỉnh ở đây KHÔNG có tác dụng: một ô bấm được mà không đổi được gì tệ hơn một ô bị khoá. */}
        {nguon !== 'redis' ? (
          <p className="rounded-lg bg-warning-50 px-3 py-2 text-xs text-warning-700">{t('troLyAiVanHanhDem.nguonEnv')}</p>
        ) : null}
        {daKep ? (
          <p role="status" className="rounded-lg bg-primary-50 px-3 py-2 text-xs text-primary-700">
            {t('troLyAiVanHanhDem.daKep')}
          </p>
        ) : null}

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <Checkbox className="self-center" checked={f.bat} onChange={(e) => dat({ bat: e.target.checked })}>
            {t('troLyAiVanHanhDem.bat')}
          </Checkbox>
          <Field
            label={t('troLyAiVanHanhDem.hanPhut')}
            hint={t('troLyAiVanHanhDem.khoangPhut', { thap: soVN(hanThapP, locale), cao: soVN(hanCaoP, locale) })}
          >
            {(p) => (
              <Input
                {...p}
                type="number"
                step="0.5"
                min={hanThapP}
                max={hanCaoP}
                value={String(giayRaPhut(f.hanGiay))}
                onChange={(e) => dat({ hanGiay: phutRaGiay(Number(e.target.value)) })}
              />
            )}
          </Field>
          <Field
            label={t('troLyAiVanHanhDem.sucChua')}
            hint={t('troLyAiVanHanhDem.khoang', { thap: soVN(chuaThap, locale), cao: soVN(chuaCao, locale) })}
          >
            {(p) => (
              <Input
                {...p}
                type="number"
                min={chuaThap}
                max={chuaCao}
                value={String(f.sucChua)}
                onChange={(e) => dat({ sucChua: Number(e.target.value) })}
              />
            )}
          </Field>
        </div>

        {/* Hai núm của tầng hai, tách bằng đường kẻ: ba núm trên nói về đệm câu TRẢ LỜI, hai núm này nói về thứ khác. */}
        <div className="grid grid-cols-1 gap-3 border-t border-default pt-3 sm:grid-cols-3">
          <div className="space-y-1">
            <Checkbox checked={Boolean(f.demTrucTiep)} onChange={(e) => dat({ demTrucTiep: e.target.checked })}>
              {t('troLyAiVanHanhDem.demTrucTiep')}
            </Checkbox>
            <p className="text-xs text-content-muted">{t('troLyAiVanHanhDem.demTrucTiepMo')}</p>
          </div>
          <Field
            label={t('troLyAiVanHanhDem.sqlHanPhut')}
            hint={
              f.sqlHanGiay
                ? t('troLyAiVanHanhDem.khoangPhut', {
                    thap: soVN(giayRaPhut(sqlThap), locale),
                    cao: soVN(giayRaPhut(sqlCao), locale),
                  })
                : t('troLyAiVanHanhDem.sqlTat')
            }
          >
            {(p) => (
              <Input
                {...p}
                type="number"
                step="5"
                min={0}
                value={String(f.sqlHanGiay ? giayRaPhut(f.sqlHanGiay) : 0)}
                onChange={(e) => dat({ sqlHanGiay: phutRaGiay(Number(e.target.value)) })}
              />
            )}
          </Field>
          {/* Gợi ý đổi theo TÌNH TRẠNG MÁY CHỦ, không chỉ theo giá trị đang gõ: nhận 5 phút rồi không đệm gì là ô nói dối. */}
          <Field
            label={t('troLyAiVanHanhDem.tiengHanPhut')}
            hint={
              !tiengChay && Boolean(f.tiengHanGiay)
                ? t('troLyAiVanHanhDem.tiengCanRedis')
                : f.tiengHanGiay
                  ? t('troLyAiVanHanhDem.khoangPhut', {
                      thap: soVN(giayRaPhut(tiengThap), locale),
                      cao: soVN(giayRaPhut(tiengCao), locale),
                    })
                  : t('troLyAiVanHanhDem.tiengTat')
            }
          >
            {(p) => (
              <Input
                {...p}
                type="number"
                step="1"
                min={0}
                value={String(f.tiengHanGiay ? giayRaPhut(f.tiengHanGiay) : 0)}
                onChange={(e) => dat({ tiengHanGiay: phutRaGiay(Number(e.target.value)) })}
              />
            )}
          </Field>
        </div>

        {thongKe ? (
          <div className="flex flex-wrap items-center gap-2 border-t border-default pt-3">
            <Badge>{t('troLyAiVanHanhDem.soMuc', { n: soVN(thongKe.soMuc, locale) })}</Badge>
            <Badge>{t('troLyAiVanHanhDem.tiLeTrung', { p: soVN(Math.round((thongKe.tiLeTrung ?? 0) * 100), locale) })}</Badge>
            <Badge>
              {thongKe.cheDo === 'redis' ? t('troLyAiVanHanhDem.cheDoRedis') : t('troLyAiVanHanhDem.cheDoTienTrinh')}
            </Badge>
            <Badge>
              {t('troLyAiVanHanhDem.demTieng', {
                gt: tiengChay ? t('troLyAiVanHanhDem.co') : t('troLyAiVanHanhDem.khong'),
              })}
            </Badge>
            {/* Cảnh báo do MÁY CHỦ soạn, phụ thuộc chế độ đang chạy — không viết cứng ở giao diện. */}
            {thongKe.canhBao ? <p className="text-xs text-content-muted">{thongKe.canhBao}</p> : null}
          </div>
        ) : null}
      </TileBody>
    </Tile>
  );
}
